import { useNavigate } from 'react-router-dom';
import { Stack, Group, Text, Badge, Box, Button } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import type { admin } from '@repletoryreach/sdk-api';
import { formatDateTime } from '../../../lib/format';
import { TenantName } from '../../../components/TenantName';
import { PersonAvatar } from '../../users/components/PersonAvatar';
import { useT } from '../../../i18n/I18nProvider';

interface Props {
  records: admin.AdminUser[];
  pendingId?: string;
  currentUserId?: string;
  onPromote: (u: admin.AdminUser) => void;
  onDemote: (u: admin.AdminUser) => void;
}

/**
 * Vertical list view: one breathable row per user with the scope action inline.
 *
 * The row itself opens the user's detail page. It used to carry the hover lift
 * of a clickable card while going nowhere, which is the worst of both.
 */
export function RolesList({ records, pendingId, currentUserId, onPromote, onDemote }: Props) {
  const t = useT();
  const navigate = useNavigate();
  const unnamed = t('users.unnamed');
  return (
    <Stack gap="sm">
      {records.map((u) => (
        <div
          key={u.id}
          className="rr-list-row"
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/users/${u.id}`)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/users/${u.id}`);
            }
          }}
        >
          <Group gap="md" align="center" wrap="nowrap" style={{ width: '100%' }}>
            <span style={{ flexShrink: 0, display: 'inline-flex' }}>
              <PersonAvatar name={u.display_name || u.email || unnamed} size="md" />
            </span>
            <Stack gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <Group gap="xs" align="baseline" wrap="nowrap">
                <Text role="subtitle" fw={600} truncate>{u.display_name || u.email || unnamed}</Text>
                {u.display_name && u.email && (
                  <Text role="caption" c="var(--rr-color-text-muted)" truncate>{u.email}</Text>
                )}
                {u.id === currentUserId && <Badge size="xs" variant="outline">{t('roles.you')}</Badge>}
              </Group>
              <Group gap="xs" align="baseline" wrap="nowrap" style={{ minWidth: 0 }}>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('common.tenant')}</Text>
                <TenantName id={u.organisation_id} subtle />
                <Text role="caption" c="var(--rr-color-text-muted)">
                  · {t('roles.list.joined', { date: formatDateTime(u.created_at) })}
                </Text>
              </Group>
            </Stack>
            <StatusBadge size="sm" status={u.scope === 'platform' ? 'warning' : 'pending'} style={{ flexShrink: 0 }}>
              {u.scope === 'platform' ? t('users.scope.platform') : t('users.scope.tenant')}
            </StatusBadge>
            <Box style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
              {u.scope === 'tenant' && (
                <Button size="xs" loading={pendingId === u.id} onClick={() => onPromote(u)}>
                  {t('roles.promote')}
                </Button>
              )}
              {u.scope === 'platform' && (
                u.id === currentUserId ? (
                  <Tooltip label={t('roles.selfDemotionBlocked')}>
                    <span style={{ display: 'inline-flex' }}>
                      <Button size="xs" variant="subtle" disabled>{t('roles.demote')}</Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button size="xs" variant="subtle" loading={pendingId === u.id} onClick={() => onDemote(u)}>
                    {t('roles.demote')}
                  </Button>
                )
              )}
            </Box>
          </Group>
        </div>
      ))}
    </Stack>
  );
}
